// --- useConnectionTemplate.ts ---
import { useCallback, useEffect, useState } from 'react';
import { Logger } from '@saviynt/common';

import type { InputFields } from '../../../components/FormBuilder/components/Form/FormBuilder';
import { type Category } from '../../ManageCMDBDiscoveredApps/data-layer/discoveryConfigByIdTypes';

import { mapFilteredResponseToInputFields } from './ManageCMDBDiscoveredAppsConnectionUtils';

/**
 * Hook to map static configuration into FormBuilder fields.
 * @param configCategories - categories array from tab.details.configuration
 * @param onInputChange - callback for field value changes
 */
const useConnectionTemplate = (
  configCategories: Category[],
  onInputChange: InputFields['inputValueStatus']
): {
  templateFields: InputFields[];
  isLoading: boolean;
  isError: boolean;
  fetchTemplate: () => Promise<void>;
} => {
  const [templateFields, setTemplateFields] = useState<InputFields[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);

  // Synchronously map config to fields, return Promise<void> for reload signature
  const fetchTemplate = useCallback((): Promise<void> => {
    setIsLoading(true);

    try {
      const mapped = mapFilteredResponseToInputFields(
        configCategories,
        onInputChange
      );

      setTemplateFields(mapped);
      setIsError(false);
    } catch (err) {
      Logger.error('Error mapping connection template fields:', err);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }

    return Promise.resolve();
  }, [configCategories, onInputChange]);

  // Auto-invoke on dependencies change
  useEffect(() => {
    fetchTemplate().catch((err) => Logger.error('Unexpected error:', err));
  }, [fetchTemplate]);

  return { templateFields, isLoading, isError, fetchTemplate };
};

export default useConnectionTemplate;
